import React, { useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { TIMEZONES } from "../../../../utils/timezones";
import { useBookingData } from "../../../../providers/BookingDataProvider";
import { useTheme } from "../../../../theme/ThemeProvider";
import Calendar from "../../../../components/core/Calendar";
import { CalendarDay } from "../../../../components/core/Calendar/Calendar.types";
import useCalendarAvailability from "../../../../hooks/calendar/useCalendarAvailability";
import CalendarViewModel from "../../../../viewModels/calendarViewModel";
import Button from "../../../../components/core/Button";
import { generateCacheKey } from "../../../../utils/cacheKeyGenerator";
// Debug component removed
// Debug system removed

interface CalendarStepProps {
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  selectedTimezone: string;
  onTimezoneChange: (timezone: string) => void;
  onMonthChange?: (date: Date) => void;
  selectedDuration?: number;
}

/**
 * CalendarStep Component
 *
 * Displays a calendar for date selection. This component focuses only on
 * calendar UI interactions while delegating data fetching and business logic
 * to the BookingDataProvider and hooks.
 */
const CalendarStep: React.FC<CalendarStepProps> = ({
  selectedDate,
  onDateSelect,
  selectedTimezone,
  onTimezoneChange,
  onMonthChange,
  selectedDuration,
}) => {
  const theme = useTheme();
  const { meetingType, cachedMonthSlots, loadingSlots, loadingInitialCalendar, firstAvailableMonth, selectedProviderId } = useBookingData();
  const { t } = useTranslation();

  // Use the availability hook to get calendar availability logic
  const { isDateAvailable, getAvailabilityDayClassNames } =
    useCalendarAvailability({
      meetingTypeId: meetingType?.id,
      meetingType: meetingType,
      selectedDate,
      cachedMonthSlots,
      selectedTimezone,
      loadingSlots, // Pass loading state to prevent weekends showing as available during timezone switch
      duration: selectedDuration || meetingType?.duration?.[0] || 30,
      selectedProviderId, // Pass provider ID for service-type cache key matching
    });

  // Handle month change to prefetch data
  const handleMonthChange = useCallback(
    (date: Date) => {
      // Let the data provider handle prefetching
      // This is a UI component, it shouldn't directly call services
      // Debug system removed

      // Validate date - if it's invalid, don't trigger prefetch
      if (!date || isNaN(date.getTime())) {
        return;
      }

      // Only pass through the month change if we actually need to prefetch
      if (meetingType?.id) {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;

        // Use generateCacheKey for consistency with BookingDataProvider and useCalendarAvailability
        const currentDuration = selectedDuration || meetingType?.duration?.[0] || 30;
        const monthKey = generateCacheKey(
          meetingType.id,
          year,
          month,
          selectedTimezone,
          currentDuration,
          {
            bufferTimeBefore: meetingType.bufferTimeBefore,
            bufferTimeAfter: meetingType.bufferTimeAfter,
            slotIncrementMinutes: meetingType.slotIncrementMinutes,
            minimumBookingNotice: meetingType.minimumBookingNotice,
            maximumBookingWindow: meetingType.maximumBookingWindow,
          },
          meetingType.scheduleId,
          selectedProviderId
        );

        // Check if we NEED to prefetch - if we don't have data or it's incomplete
        // AND make sure we're not retrying a month that has an error
        if (
          (!cachedMonthSlots[monthKey] ||
            !cachedMonthSlots[monthKey].isComplete) &&
          !(cachedMonthSlots[monthKey] && cachedMonthSlots[monthKey].error)
        ) {
          // Debug system removed

          // Only call the parent prefetch handler if we need to
          if (onMonthChange && typeof onMonthChange === "function") {
            // Clone the date to prevent any reference issues
            const newDate = new Date(date);

            // Use requestAnimationFrame to add some delay and prevent
            // multiple rapid requests when navigating through months
            requestAnimationFrame(() => {
              onMonthChange(newDate);
            });
          }
        } else if (
          cachedMonthSlots[monthKey] &&
          cachedMonthSlots[monthKey].error
        ) {
          // Debug system removed
        } else {
          // Debug system removed
        }
      }
    },
    [meetingType?.id, selectedTimezone, cachedMonthSlots, onMonthChange, selectedDuration, selectedProviderId, meetingType]
  );

  // Custom renderer for day content
  const renderCalendarDay = useCallback((day: CalendarDay) => {
    // Only show availability styling for dates within the current month
    const isAvailable = day.isCurrentMonth && isDateAvailable(day.date);

    // Simple day renderer with today indicator and proper circular selection
    // Uses CSS variables for dynamic meeting type colors
    return (
      <div className="w-full h-full flex flex-col items-center justify-center relative">
        <span
          className={`
            flex items-center justify-center w-9 h-9 rounded-full font-medium
            ${day.isSelected ? '' : isAvailable ? 'transition-colors duration-200' : ''}
          `}
          style={
            day.isSelected
              ? {
                  backgroundColor: "var(--brand-color, #2E7AA0)",
                  color: "var(--brand-text-color, white)",
                }
              : isAvailable
                ? {
                    backgroundColor: "var(--brand-color-very-light, #E8F4FA)",
                    color: "var(--brand-color, #2E7AA0)",
                  }
                : undefined
          }
          onMouseEnter={(e) => {
            if (!day.isSelected && isAvailable) {
              e.currentTarget.style.backgroundColor = "var(--brand-color-light, #A3D3EB)";
            }
          }}
          onMouseLeave={(e) => {
            if (!day.isSelected && isAvailable) {
              e.currentTarget.style.backgroundColor = "var(--brand-color-very-light, #E8F4FA)";
            }
          }}
        >
          {day.date.getDate()}
        </span>
        {day.isToday && !day.isSelected && (
          <div
            className="absolute bottom-1 w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: "var(--brand-color, #2E7AA0)" }}
          ></div>
        )}
      </div>
    );
  }, [isDateAvailable]);

  // Debug timezone call removed

  return (
    <div className="h-full flex flex-col relative">
      {/* Calendar component */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full">
            <Calendar
              selectedDate={selectedDate}
              onDateSelect={onDateSelect}
              onMonthChange={handleMonthChange}
              isDateSelectable={isDateAvailable}
              dayRenderer={renderCalendarDay}
              isLoading={loadingSlots || loadingInitialCalendar}
              className="w-full h-full"
              timezone={selectedTimezone}
              initialMonth={firstAvailableMonth || undefined}
            />
        </div>
      </div>
    </div>
  );
};

export default CalendarStep;
